import { useMemo } from 'react';
import { FormatDateTimeBr } from '../../utils/date';
import { FormatMoney } from '../../utils/format';
import { DASH, DeltaMoney, FormatPercentOrDash, IsNum, MoneyPrefix, PercentPrefix } from './campaignCardShared';
import { SortableHeader, useTableSort } from './useTableSort';

/**
 * Lista compacta INLINE de todas as campanhas elegíveis do anúncio — substitui o
 * antigo modal "Ver Campanhas".
 *
 *   Campanha | Consumer | Líquido | Rebate | Desc%
 *
 * Os INDICADORES DE ECONOMIA não têm coluna própria: viram PREFIXO do valor que
 * modificam (regra do dono) — `consumer_savings` antes do Consumer e
 * `seller_gain` antes do Líquido; o `Meli%` virou prefixo do Rebate em R$. Assim
 * cada número aparece junto do valor a que se refere, sem coluna extra.
 *
 * Tudo vem calculado do backend (CONTRATO-MONITOR.md §Adições da 2ª rodada) —
 * nada é recalculado aqui. Linha ATUAL (`is_current`) e MELHOR (`is_best`)
 * ganham destaque de fundo.
 */

// Desc% deixou de ser a coluna mais estreita: agora carrega o desconto em R$
// como prefixo do percentual, e com 0.6fr o par quebrava em duas linhas.
const GRID_COLS = 'grid grid-cols-[minmax(0,1.8fr)_1.1fr_1.1fr_0.9fr_1fr] gap-2';

/**
 * Colunas ordenáveis. `value` extrai o DADO de comparação, não o texto exibido —
 * ordenar "R$ 1.234,00" como string colocaria R$ 9,00 depois de R$ 1.234,00.
 */
const COLUMNS = [
    {
        key: 'campaign',
        label: 'Campanha',
        align: 'left',
        title: 'Nome da campanha',
        value: (c) => c?.campaign_name || c?.campaign_id || '',
    },
    {
        key: 'consumer',
        label: 'Consumer',
        align: 'right',
        title: 'Preço ao consumidor (prefixo = quanto o preço muda vs. a campanha atual)',
        value: (c) => c?.price_consumer,
    },
    {
        key: 'net',
        label: 'Líquido',
        align: 'right',
        title: 'Meu líquido (prefixo = quanto EU ganho vs. a atual)',
        value: (c) => c?.price_net,
    },
    {
        key: 'rebate',
        label: 'Rebate',
        align: 'right',
        title: 'Rebate do Meli (prefixo = percentual)',
        value: (c) => c?.rebate_amount,
    },
    {
        key: 'discount',
        label: 'Desc%',
        align: 'right',
        title: 'Desconto sobre o preço base (prefixo = o mesmo desconto em R$)',
        value: (c) => c?.discount_percent,
    },
];

/**
 * Desconto em R$ da campanha: `price_base − price_consumer`, com a base DESTA
 * campanha (o `original_price` da participação pode diferir do preço cheio do
 * anúncio — usar a base de outra linha daria um valor errado). Falta qualquer
 * um dos dois lados → null, que a UI mostra como '—'; nunca 0 inventado.
 */
const discountAmount = (campaign) => {
    if (!IsNum(campaign?.price_base) || !IsNum(campaign?.price_consumer)) {
        return null;
    }
    return Number(campaign.price_base) - Number(campaign.price_consumer);
};

/** Ordem quando nenhuma coluna está selecionada: líquido desc, leitura do Legacy. */
const defaultSort = (list) => list.sort((a, b) => (Number(b?.price_net) || 0) - (Number(a?.price_net) || 0));

const CampaignItemPromotions = ({ campaigns }) => {
    const { sort, toggleSort, sortRows } = useTableSort(COLUMNS);

    const rows = useMemo(
        () => sortRows(Array.isArray(campaigns) ? campaigns : [], defaultSort),
        [campaigns, sortRows],
    );

    if (rows.length === 0) {
        return <p className="px-1 py-1.5 text-[11px] text-mono-400">Nenhuma campanha elegível para este anúncio.</p>;
    }

    return (
        <div className="overflow-x-auto">
            {/* 8 → 5 colunas (os deltas viraram prefixos): cabe sem scroll
                horizontal. 620px porque Desc% agora leva desconto em R$ + %. */}
            <div className="min-w-[620px]">
                <SortableHeader columns={COLUMNS} sort={sort} onToggle={toggleSort} gridClass={GRID_COLS} />

                {rows.map((campaign) => {
                    const isCurrent = campaign?.is_current === true;
                    const isBest = campaign?.is_best === true && !isCurrent;
                    const isStarted = campaign?.item_status === 'started';
                    const rowTone = isCurrent
                        ? 'bg-warning-soft/60 dark:bg-warning-900/10'
                        : isBest
                          ? 'bg-success-soft/60 dark:bg-success-900/10'
                          : '';

                    return (
                        <div
                            key={campaign?.campaign_id || campaign?.campaign_name}
                            className={`${GRID_COLS} items-center border-b border-mono-100 px-1 py-1 text-[11px] tabular-nums last:border-b-0 dark:border-mono-900 ${rowTone}`}
                        >
                            <div className="min-w-0">
                                {/* O nome e as pílulas ficam em FLEX, não no mesmo
                                    parágrafo truncado. Com tudo dentro de um <p
                                    className="truncate">, um nome longo ("Saia na frente
                                    da concorrência e reduza suas tarifas de venda")
                                    consumia a largura inteira e a pílula ATUAL/MELHOR
                                    era recortada pelo overflow — sumia da coluna.
                                    Agora só o nome tem `truncate` (com `min-w-0`, senão
                                    o flex não deixa encolher) e as pílulas têm
                                    `shrink-0`: elas nunca encolhem, e o nome cede
                                    exatamente o espaço necessário. `title` preserva o
                                    nome inteiro no hover, já que a reticência esconde. */}
                                <div className="flex items-center gap-1">
                                    <span
                                        className="min-w-0 truncate font-semibold text-black dark:text-white"
                                        title={campaign?.campaign_name || campaign?.campaign_id || undefined}
                                    >
                                        {campaign?.campaign_name || campaign?.campaign_id || DASH}
                                    </span>
                                    {isCurrent ? (
                                        <span className="shrink-0 rounded-md bg-warning-500 px-1 py-px text-[9px] font-bold uppercase text-black">
                                            atual
                                        </span>
                                    ) : null}
                                    {isBest ? (
                                        <span className="shrink-0 rounded-md bg-success-500 px-1 py-px text-[9px] font-bold uppercase text-black">
                                            melhor
                                        </span>
                                    ) : null}
                                    {campaign?.is_new === true ? (
                                        <span
                                            className="shrink-0 rounded-md bg-info-500 px-1 py-px text-[9px] font-bold uppercase text-white"
                                            title={campaign?.first_seen_at ? `Apareceu para este anúncio em ${FormatDateTimeBr(campaign.first_seen_at)}` : undefined}
                                        >
                                            nova
                                        </span>
                                    ) : null}
                                </div>
                                <p className="truncate font-mono text-[9px] text-mono-400">
                                    {campaign?.campaign_id || DASH} · {campaign?.campaign_type || DASH} ·{' '}
                                    {/* `started` = o anúncio JÁ ESTÁ participando (os outros
                                        valores são `candidate` e `pending`, que são
                                        elegibilidade, não adesão). Fica AQUI, no lugar onde
                                        o status já era mostrado, e não como pílula solta lá
                                        em cima — o destaque é a cor verde do "Ativo" do
                                        header, sem fundo, para não roubar espaço da linha.
                                        Não confundir com "atual": pode haver várias started,
                                        mas só uma define o preço de venda. */}
                                    {isStarted ? (
                                        <span
                                            className="font-bold text-success-text dark:text-success-300"
                                            title="O anúncio está participando desta campanha"
                                        >
                                            started
                                        </span>
                                    ) : (
                                        campaign?.item_status || DASH
                                    )}
                                </p>
                            </div>
                            {/* Delta = PREFIXO do valor que ele modifica (sem coluna própria).
                                `isCurrentRow`: só a linha da campanha ATUAL mostra '—' —
                                nela a comparação é consigo mesma. Outra campanha que empata
                                mostra "R$ 0,00" ("dá na mesma" ≠ "não se aplica"). */}
                            <span className="flex items-baseline justify-end gap-1">
                                {/* `signReflectsPrice`: o sinal descreve o movimento do PREÇO
                                    (economia = fica mais barato = "−"), não o da economia. */}
                                <DeltaMoney
                                    value={campaign?.consumer_savings}
                                    isCurrentRow={isCurrent}
                                    signReflectsPrice
                                    dashWhenAbsent={IsNum(campaign?.price_consumer)}
                                    title="Quanto o preço ao consumidor muda se esta campanha for ativada (vs. a atual)"
                                    zeroTitle="É a campanha atual — não há o que comparar"
                                />
                                {/* Candidatura sem preço acordado entra na comparação
                                    pela SUGESTÃO do Mercado Livre (migração 0021).
                                    O til marca a estimativa: exibir sugestão com a
                                    mesma cara de oferta fechada seria mentir sobre o
                                    compromisso que existe do outro lado. */}
                                <span
                                    className="truncate text-black dark:text-white"
                                    title={
                                        campaign?.price_is_suggested
                                            ? 'Preço SUGERIDO pelo Mercado Livre para esta candidatura, não um valor acordado'
                                            : undefined
                                    }
                                >
                                    {campaign?.price_is_suggested ? '~' : ''}
                                    {FormatMoney(campaign?.price_consumer)}
                                </span>
                            </span>
                            <span className="flex items-baseline justify-end gap-1">
                                <DeltaMoney
                                    value={campaign?.seller_gain}
                                    isCurrentRow={isCurrent}
                                    dashWhenAbsent={IsNum(campaign?.price_net)}
                                    title="Variação do MEU líquido se esta campanha for ativada (vs. a atual)"
                                    zeroTitle="É a campanha atual — não há o que comparar"
                                />
                                {/* Sem bold: o Líquido é uma coluna como as outras — o
                                    destaque de "qual linha importa" já é do fundo
                                    (atual/melhor), não de uma coluna inteira em negrito. */}
                                <span className="truncate text-black dark:text-white">
                                    {FormatMoney(campaign?.price_net)}
                                </span>
                            </span>
                            <span className="flex items-baseline justify-end gap-1">
                                <PercentPrefix value={campaign?.rebate_percent} title="Percentual de rebate do Meli" />
                                <span className="truncate text-black dark:text-white">
                                    {FormatMoney(campaign?.rebate_amount)}
                                </span>
                            </span>
                            {/* Desc%: mesmo padrão do card — o auxiliar (desconto em R$)
                                vem ANTES do valor principal, em 9px. */}
                            <span className="flex items-baseline justify-end gap-1">
                                <MoneyPrefix
                                    value={discountAmount(campaign)}
                                    dashWhenAbsent={IsNum(campaign?.discount_percent)}
                                    title="Desconto em reais (preço base − preço ao consumidor)"
                                />
                                {/* Mesma cor dos demais valores (Rebate, Consumer): era a
                                    única coluna em cinza e no tema escuro sumia. */}
                                <span className="truncate text-black dark:text-white">
                                    {FormatPercentOrDash(campaign?.discount_percent)}
                                </span>
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CampaignItemPromotions;
